import {
  AnchorHTMLAttributes,
  FC,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Link, useLocation, useParams } from '@remix-run/react'

import {
  AnchorLinks,
  Container,
  PageHeading,
  PrimaryButton,
  SectionHeading,
  SeeMoreButton,
  SubSectionHeading,
} from '~/components'
import { IconSmallLogo } from '~/components/ui/icons/small-logo'
import { NO_IMAGE_URL } from '~/constants'
import { ContactCtaSection, SpaceCard } from '~/features/common'
import {
  earthquakeResistanceStandardToJaText,
  officeOtherAccessesToOtherAccessTextList,
  officeSpacesToFacilityTextList,
  officeStationsToStationTextList,
  structureToJaText,
} from '~/features/office-detail-page/helpers/convert-to-text'
import { useAnchorLinks } from '~/features/office-detail-page/hooks/use-anchor-links'
import { twMerge } from '~/lib'
import { Office } from '~/models/types'
import { encodeImageUrl } from '~/utils/encode-image-url'

import { OfficeDetailMap } from '../components'

type OfficeDetailPageProps = {
  office: Office
}

const INITIAL_MAX_PLANS_COUNT = 5

type InnerContainerProps = PropsWithChildren & {
  className?: string
}

const InnerContainer: FC<InnerContainerProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge('container mx-auto lg:max-w-none lg:px-0', className)}
    >
      {children}
    </div>
  )
}

const OuterContainer: FC<PropsWithChildren> = ({ children }) => {
  return <div className="mx-auto min-[844px]:max-w-[844px]">{children}</div>
}

type LinkToMapProps = {
  href: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

// NOTE: LinkToMapを使いたかったが、オフィス名を折り返したくなく、アイコンを縦に並べるために独自定義した。LinkToMapのvariantsはminiを参考にした。
const LinkToMapButton: FC<LinkToMapProps> = ({ href, ...props }) => {
  return (
    <a
      className="flex items-center justify-center rounded border border-grayBtn bg-white hover:opacity-70 md:h-[52px] md:w-[147px]"
      href={href}
      {...props}
    >
      <div className="flex flex-col items-center justify-center gap-x-2 gap-y-1 p-2 text-brand md:flex-row md:p-0">
        <IconSmallLogo height={26} width={21} />
        <span className="text-xs font-bold md:text-body">地図に戻る</span>
      </div>
    </a>
  )
}

export const OfficeDetailPage: FC<OfficeDetailPageProps> = ({ office }) => {
  const { prefectureSlug, officeId } = useParams<
    'prefectureSlug' | 'officeId'
  >()

  const sortedImages = office.images.sort((a, b) => a.priority - b.priority)
  const firstImage =
    sortedImages.find((image) => image.isEyeCatch) || sortedImages[0]
  const [isPlansOmitted, setIsPlansOmitted] = useState(
    office.spaces.length > INITIAL_MAX_PLANS_COUNT,
  )
  const planLength = office.spaces.length

  const {
    links,
    accessSectionId,
    accessSectionRef,
    facilitySectionId,
    facilitySectionRef,
    planSectionId,
    planSectionRef,
    pictureSectionId,
    pictureSectionRef,
  } = useAnchorLinks()

  const stationsTextList = officeStationsToStationTextList(office.stations)
  const otherAccessesTextList = officeOtherAccessesToOtherAccessTextList(
    office.otherAccesses,
  )
  const facilitiesTextList = officeSpacesToFacilityTextList(office.spaces)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const mapVisited = queryParams.get('mapVisited')
  const isMapVisited = mapVisited === 'true'
  // NOTE: 地図画面に戻る際はmapVisitedは必要ないので削除
  queryParams.delete('mapVisited')
  queryParams.set('initialPinnedOfficeId', String(officeId))
  // NOTE: 地図画面に戻るときに検索条件を保持するのと、初期表示時にドロワー内でオフィス詳細画面を表示するため。
  const officeDetailLinkWithQueryParams = `/map?${queryParams.toString()}`
  const [isDescriptionOmitted, setIsDescriptionOmitted] = useState(true)
  const descriptionRef = useRef<HTMLParagraphElement | null>(null)
  const [isClamped, setIsClamped] = useState(false)

  useEffect(() => {
    const el = descriptionRef.current
    if (!el) return
    setIsClamped(el.scrollHeight > el.clientHeight + 1)
  }, [office.description, isDescriptionOmitted])

  return (
    <main className="relative pt-6 md:pt-10">
      <meta content={office.name} property="og:title" />
      <meta content={office.description || ''} property="og:description" />
      <meta content="place" property="og:type" />
      <meta
        content={`/prefectures/${prefectureSlug}/offices/${officeId}`}
        property="og:url"
      />
      <meta
        content={firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL}
        property="og:image"
      />

      <div className="fixed bottom-0 z-20 w-full border-t border-grayBorder bg-white p-2.5 text-center">
        <Container>
          <Link
            rel="noreferrer"
            target="_blank"
            to={`/prefectures/${prefectureSlug}/offices/${officeId}/contact`}
          >
            <PrimaryButton className="w-full max-w-[454px]" color="cta">
              お問い合わせ・内見予約
            </PrimaryButton>
          </Link>
        </Container>
      </div>
      <OuterContainer>
        <InnerContainer className="mb-4 flex flex-row items-center justify-between md:mb-10">
          <PageHeading>{office.name}</PageHeading>
          {/* NOTE: 地図画面のドロワー内オフィス詳細画面から遷移してきた場合、戻るボタンを用意してクエリパラメータも保持する。 */}
          {isMapVisited && (
            <LinkToMapButton href={officeDetailLinkWithQueryParams} />
          )}
        </InnerContainer>
        <div className="mb-6 h-[250px] md:mb-10 md:h-[unset]">
          <img
            alt={`${office.name}の写真`}
            className="h-full w-full object-cover"
            src={firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL}
          />
        </div>
        <AnchorLinks className="sticky top-0 z-10" links={links} />
        <section id={accessSectionId} ref={accessSectionRef}>
          <InnerContainer>
            <div className="border-grayBorder py-6 md:border-b md:py-10">
              <SectionHeading className="mb-4 md:mb-6" color="brand">
                アクセス
              </SectionHeading>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">住所</SubSectionHeading>
                <p className="text-body">
                  {office.address}
                  <br />
                  {office.building}
                </p>
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">最寄駅</SubSectionHeading>
                <ul className="list-inside list-disc text-body">
                  {stationsTextList.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                  {otherAccessesTextList.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                  {stationsTextList.length === 0 &&
                    otherAccessesTextList.length === 0 && (
                      <p className="text-body">-</p>
                    )}
                </ul>
              </div>
              <OfficeDetailMap
                className="ml-[calc(50%-50vw)] w-screen md:ml-0 md:w-full"
                latitude={office.latitude}
                longitude={office.longitude}
              />
            </div>
          </InnerContainer>
        </section>
        <section
          className="bg-brand-bg-light md:bg-white"
          id={facilitySectionId}
          ref={facilitySectionRef}
        >
          <InnerContainer>
            <div className="border-grayBorder py-6 md:border-b md:py-10">
              <SectionHeading className="mb-4 md:mb-6" color="brand">
                特徴・設備
              </SectionHeading>
              <div className="mb-6">
                {isDescriptionOmitted ? (
                  <>
                    <p
                      className="overflow-hidden whitespace-pre-line [-webkit-box-orient:vertical] [-webkit-line-clamp:10] [display:-webkit-box]"
                      ref={descriptionRef}
                    >
                      {office.description}
                    </p>
                    {isClamped && (
                      <SeeMoreButton
                        className="mx-auto mt-4 md:mx-0"
                        onClick={() => setIsDescriptionOmitted(false)}
                        title="もっと見る"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <p className="whitespace-pre-line">{office.description}</p>
                    {isClamped && (
                      <SeeMoreButton
                        className="mx-auto mt-4 md:mx-0"
                        onClick={() => setIsDescriptionOmitted(true)}
                        title="閉じる"
                      />
                    )}
                  </>
                )}
              </div>
              <div className="mb-4 md:mb-6">
                <SubSectionHeading className="mb-1">
                  設備・サービス
                </SubSectionHeading>
                {facilitiesTextList.length === 0 ? (
                  <p className="text-body">-</p>
                ) : (
                  <>
                    <ul className="mb-1 list-inside list-disc text-body">
                      {facilitiesTextList.map((text) => (
                        <li key={text}>{text}</li>
                      ))}
                    </ul>
                    <p className="text-body-s">
                      ※プランによって異なる場合があります
                    </p>
                  </>
                )}
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">営業時間</SubSectionHeading>
                <p className="whitespace-pre-line text-body">
                  {office.businessHours || '-'}
                </p>
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">
                  竣工（築年）
                </SubSectionHeading>
                <p className="text-body">{office.completionYear}年</p>
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">耐震</SubSectionHeading>
                <p className="text-body">
                  {office.earthquakeResistanceStandard
                    ? earthquakeResistanceStandardToJaText(
                        office.earthquakeResistanceStandard,
                      )
                    : '-'}
                </p>
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">構造</SubSectionHeading>
                <p className="text-body">
                  {office.structure ? structureToJaText(office.structure) : ''}
                </p>
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">規模</SubSectionHeading>
                <p className="text-body">{office.floors}</p>
              </div>
              <div className="mb-4">
                <SubSectionHeading className="mb-1">広さ</SubSectionHeading>
                <p className="text-body">
                  {office.size && office.size !== 0 ? `${office.size}㎡` : '-'}
                </p>
              </div>
            </div>
          </InnerContainer>
        </section>
        <section id={planSectionId} ref={planSectionRef}>
          <InnerContainer>
            <div className="border-grayBorder py-6 md:border-b md:py-10">
              <SectionHeading className="mb-4 md:mb-6" color="brand">
                プラン
              </SectionHeading>
              {isPlansOmitted ? (
                <div className={twMerge('space-y-4', 'mb-4')}>
                  {office.spaces
                    .toSpliced(INITIAL_MAX_PLANS_COUNT)
                    .map((space) => (
                      <SpaceCard key={space.id} space={space} />
                    ))}
                </div>
              ) : (
                <div className={twMerge('space-y-4', 'mb-4')}>
                  {office.spaces.map((space) => (
                    <SpaceCard key={space.id} space={space} />
                  ))}
                  {planLength > INITIAL_MAX_PLANS_COUNT && (
                    <SeeMoreButton
                      className="mx-auto md:mx-0"
                      onClick={() => setIsPlansOmitted(true)}
                      title="閉じる"
                    />
                  )}
                </div>
              )}
              {isPlansOmitted && (
                <SeeMoreButton
                  className="mx-auto md:mx-0"
                  onClick={() => setIsPlansOmitted(false)}
                  title="もっと見る"
                />
              )}
            </div>
          </InnerContainer>
        </section>
        <section
          className="mb-16 bg-brand-bg-light md:mb-0 md:bg-white"
          id={pictureSectionId}
          ref={pictureSectionRef}
        >
          <InnerContainer>
            <div className="py-6 md:py-10">
              <SectionHeading className="mb-4 md:mb-6" color="brand">
                写真
              </SectionHeading>
              <div className="flex flex-wrap gap-4">
                {office.images.length === 0 && (
                  <p className="text-body">
                    このオフィスの写真が見つかりませんでした。
                  </p>
                )}
                {office.images
                  .sort((a, b) => a.priority - b.priority)
                  .map((image) => (
                    <div
                      className="h-[228px] basis-full md:basis-[calc((100%-16px)/2)]"
                      key={image.id}
                    >
                      <img
                        alt={`${office.name}の写真`}
                        className="h-full w-full object-cover"
                        src={encodeImageUrl(image.url)}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </InnerContainer>
        </section>
        <InnerContainer>
          <ContactCtaSection
            className="mb-10"
            href={`/prefectures/${prefectureSlug}/offices/${officeId}/contact`}
          >
            <p>プランやお部屋がお決まりでない方、</p>
            <p>実際にオフィスを見学してみたい方はこちら</p>
          </ContactCtaSection>
        </InnerContainer>
      </OuterContainer>
    </main>
  )
}
